import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../services/api';
import {
  BarChart3, TrendingUp, Download, Calendar, Users, Bus, CreditCard,
  CheckCircle, Clock, RefreshCw, FileSpreadsheet, FileText, DollarSign,
  TrendingDown, Activity, Target
} from 'lucide-react';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { it } from 'date-fns/locale';

const Report = () => {
  const [loading, setLoading] = useState(true);
  const [kpiData, setKpiData] = useState(null);
  const [abbonamentiPerLinea, setAbbonamentiPerLinea] = useState([]);
  const [abbonamentiPerPeriodo, setAbbonamentiPerPeriodo] = useState([]);
  const [entrateMensili, setEntrateMensili] = useState([]);
  const [tassoRinnovo, setTassoRinnovo] = useState([]);
  
  // Filtri per export
  const [exportFormat, setExportFormat] = useState('excel');
  const [exportDataInizio, setExportDataInizio] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [exportDataFine, setExportDataFine] = useState(format(new Date(), 'yyyy-MM-dd'));

  useEffect(() => {
    caricaReport();
  }, []);

  const caricaReport = async () => {
    try {
      setLoading(true);

      const [
        kpiRes,
        lineaRes,
        periodoRes,
        entrateRes,
        rinnovoRes
      ] = await Promise.all([
        api.get('/abbonamenti/report/kpi/'),
        api.get('/abbonamenti/report/abbonamenti_per_linea/'),
        api.get('/abbonamenti/report/abbonamenti_per_periodo/'),
        api.get('/abbonamenti/report/entrate_mensili/'),
        api.get('/abbonamenti/report/tasso_rinnovo_dettagliato/')
      ]);

      setKpiData(kpiRes.data);
      setAbbonamentiPerLinea(lineaRes.data);
      setAbbonamentiPerPeriodo(periodoRes.data);
      setEntrateMensili(entrateRes.data);
      setTassoRinnovo(rinnovoRes.data);
    } catch (error) {
      console.error('Errore caricamento report:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportAbbonamenti = async () => {
    try {
      const params = new URLSearchParams({
        formato: exportFormat,
        data_inizio: exportDataInizio,
        data_fine: exportDataFine
      });

      const response = await api.get(`/abbonamenti/report/export_abbonamenti/?${params}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `abbonamenti.${exportFormat === 'excel' ? 'xlsx' : 'csv'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Errore export abbonamenti:', error);
      alert('Errore durante l\'export');
    }
  };

  const handleExportPagamenti = async () => {
    try {
      const params = new URLSearchParams({
        formato: exportFormat,
        data_inizio: exportDataInizio,
        data_fine: exportDataFine
      });

      const response = await api.get(`/abbonamenti/report/export_pagamenti/?${params}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pagamenti.${exportFormat === 'excel' ? 'xlsx' : 'csv'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Errore export pagamenti:', error);
      alert('Errore durante l\'export');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <RefreshCw className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Caricamento report...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-blue-600" />
              Report e Analisi
            </h1>
            <p className="text-gray-600 mt-1">Dashboard completa con KPI e metriche</p>
          </div>
          <button
            onClick={caricaReport}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Aggiorna
          </button>
        </div>

        {/* KPI Cards */}
        {kpiData && (
          <>
            {/* Prima Riga - KPI Abbonamenti */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-600">Abbonamenti Attivi</p>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{kpiData.abbonamenti_attivi}</p>
                <p className="text-sm text-gray-500 mt-1">
                  su {kpiData.totale_abbonamenti} totali
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-600">Tasso Rinnovo</p>
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{kpiData.tasso_rinnovo}%</p>
                <p className="text-sm text-gray-500 mt-1">
                  Ultimi 3 mesi
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-600">In Scadenza</p>
                  <Clock className="w-5 h-5 text-orange-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{kpiData.abbonamenti_in_scadenza}</p>
                <p className="text-sm text-gray-500 mt-1">
                  Prossimi 7 giorni
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-600">Clienti Attivi</p>
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{kpiData.clienti_attivi}</p>
                <p className="text-sm text-gray-500 mt-1">
                  +{kpiData.nuovi_clienti_mese} questo mese
                </p>
              </div>
            </div>

            {/* Seconda Riga - KPI Finanziari */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-green-800">Entrate Mese Corrente</p>
                  <DollarSign className="w-5 h-5 text-green-700" />
                </div>
                <p className="text-3xl font-bold text-green-900">
                  €{parseFloat(kpiData.entrate_mese_corrente).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  {kpiData.tasso_crescita_entrate >= 0 ? (
                    <TrendingUp className="w-4 h-4 text-green-700" />
                  ) : (
                    <TrendingDown className="w-4 h-4 text-red-700" />
                  )}
                  <p className={`text-sm font-semibold ${kpiData.tasso_crescita_entrate >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {kpiData.tasso_crescita_entrate > 0 ? '+' : ''}{kpiData.tasso_crescita_entrate}% vs mese scorso
                  </p>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-600">Entrate Totali</p>
                  <Activity className="w-5 h-5 text-gray-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">
                  €{parseFloat(kpiData.entrate_totali).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {kpiData.pagamenti_completati} pagamenti
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-600">Conversion Rate</p>
                  <Target className="w-5 h-5 text-blue-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{kpiData.conversion_rate}%</p>
                <p className="text-sm text-gray-500 mt-1">
                  {kpiData.pagamenti_in_attesa} in attesa
                </p>
              </div>
            </div>
          </>
        )}

        {/* Abbonamenti per Linea */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Bus className="w-5 h-5 text-blue-600" />
            Abbonamenti per Linea
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Linea</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700">Totale</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700">Attivi</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700">Scaduti</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700">Entrate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {abbonamentiPerLinea.map((linea, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{linea.linea_nome}</td>
                    <td className="px-4 py-3 text-sm text-center text-gray-700">{linea.totale_abbonamenti}</td>
                    <td className="px-4 py-3 text-sm text-center">
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                        {linea.abbonamenti_attivi}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-center text-gray-500">{linea.abbonamenti_scaduti}</td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">
                      €{parseFloat(linea.entrate_totali || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Entrate Mensili */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-green-600" />
            Entrate Mensili (Ultimi 12 Mesi)
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Mese</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700">Entrate</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700">N° Pagamenti</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700">Media/Pagamento</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {entrateMensili.slice(-6).map((mese, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{mese.mese}</td>
                    <td className="px-4 py-3 text-sm text-right font-bold text-green-700">
                      €{parseFloat(mese.entrate).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 text-sm text-center text-gray-700">{mese.numero_pagamenti}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700">
                      €{parseFloat(mese.media_per_pagamento).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Tasso Rinnovo Dettagliato */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-purple-600" />
            Analisi Tasso di Rinnovo
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Periodo</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700">Scaduti</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700">Rinnovati</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700">Tasso</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700">Entrate Rinnovi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {tassoRinnovo.map((periodo, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{periodo.periodo}</td>
                    <td className="px-4 py-3 text-sm text-center text-gray-700">{periodo.abbonamenti_scaduti}</td>
                    <td className="px-4 py-3 text-sm text-center text-green-700 font-semibold">{periodo.abbonamenti_rinnovati}</td>
                    <td className="px-4 py-3 text-sm text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                        periodo.tasso_rinnovo >= 80 ? 'bg-green-100 text-green-800' :
                        periodo.tasso_rinnovo >= 60 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {periodo.tasso_rinnovo}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">
                      €{parseFloat(periodo.entrate_rinnovi).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Export Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Download className="w-5 h-5 text-blue-600" />
            Export Dati
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Filtri Export */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Formato Export</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="formato"
                      value="excel"
                      checked={exportFormat === 'excel'}
                      onChange={(e) => setExportFormat(e.target.value)}
                      className="text-blue-600"
                    />
                    <FileSpreadsheet className="w-4 h-4 text-green-600" />
                    <span className="text-sm">Excel (.xlsx)</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="formato"
                      value="csv"
                      checked={exportFormat === 'csv'}
                      onChange={(e) => setExportFormat(e.target.value)}
                      className="text-blue-600"
                    />
                    <FileText className="w-4 h-4 text-blue-600" />
                    <span className="text-sm">CSV</span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data Inizio</label>
                  <input
                    type="date"
                    value={exportDataInizio}
                    onChange={(e) => setExportDataInizio(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data Fine</label>
                  <input
                    type="date"
                    value={exportDataFine}
                    onChange={(e) => setExportDataFine(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Pulsanti Export */}
            <div className="space-y-3">
              <button
                onClick={handleExportAbbonamenti}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 font-semibold"
              >
                <Download className="w-5 h-5" />
                Export Abbonamenti
              </button>
              <button
                onClick={handleExportPagamenti}
                className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2 font-semibold"
              >
                <Download className="w-5 h-5" />
                Export Pagamenti
              </button>
              <p className="text-xs text-gray-500 text-center mt-2">
                I file saranno scaricati nel formato selezionato con i filtri applicati
              </p>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Report;
